import { useEffect, useMemo } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { Plus } from 'lucide-react'
import { useSearchParamPatch } from '../../hooks/useSearchParamPatch'
import { useAuth } from '../../hooks/useAuth'
import { useEventsByIds } from '../../hooks/useEventsByIds'
import { useGroups } from '../../hooks/useGroups'
import { useGroupsEvents } from '../../hooks/useGroupsEvents'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { useNextEventPerGroup } from '../../hooks/useNextEventPerGroup'
import type { FriendEventInterestBuckets } from '../../hooks/useFriendEventInterests'
import type { EventInterestStatus } from '../../hooks/useUserEventInterests'
import { createGroupPrompt } from '../../lib/guestPrompts'
import { createGroupLabel } from '../../lib/commonStrings'
import { placeholders } from '../../lib/skeletonUtils'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { ErrorState } from '../../components/ErrorState'
import { CrewGroupRow } from '../../components/CrewGroupRow'
import { GroupUpsellCard } from '../../components/GroupUpsellCard'
import { Section } from '../../components/Section'
import { CreateGroupDialog } from '../../components/CreateGroupDialog'
import { Button } from '../../components/ui/button'
import type { UserProfile } from '../../types'

/** Crew's groups: compact rows, the soonest next linked event first. */
export function GroupsSection({
  createOpen,
  onCreateOpenChange,
  friendInterests,
  myStatuses,
  profiles,
}: {
  createOpen: boolean
  onCreateOpenChange: (open: boolean) => void
  friendInterests: Map<string, FriendEventInterestBuckets>
  myStatuses: Map<string, EventInterestStatus>
  profiles: Record<string, UserProfile>
}) {
  const { user, signedInUid, loading: authLoading } = useAuth()
  const { groups, groupIds, loading, error, createGroup } = useGroups(user?.uid ?? null)
  const navigate = useTransitionNavigate()
  const [searchParams, patchSearchParams] = useSearchParamPatch()
  const { locale } = useLocaleContext()
  const { isGuest, prompt } = useGuestGuard()

  useEffect(() => {
    if (searchParams.get('create') !== 'group') return
    if (!signedInUid) return
    onCreateOpenChange(true)
    patchSearchParams({ create: null })
  }, [searchParams, signedInUid, patchSearchParams, onCreateOpenChange])

  const { data: allGroupEvents } = useGroupsEvents(groupIds)
  const linkedEventIds = useMemo(() => allGroupEvents.map(ge => ge.eventId), [allGroupEvents])
  const { events } = useEventsByIds(linkedEventIds)
  const nextEventPerGroup = useNextEventPerGroup(groups, allGroupEvents, events)

  const rows = useMemo(() => {
    const goingAt = (eventId: string) => {
      const going = new Set(friendInterests.get(eventId)?.going ?? [])
      if (signedInUid && myStatuses.get(eventId) === 'going') going.add(signedInUid)
      return going
    }
    return groups
      .map(group => {
        const next = nextEventPerGroup.get(group.id)
        const going = next ? goingAt(next.id) : new Set<string>()
        const membersGoing = new Set(group.memberIds.filter(uid => going.has(uid)))
        return { group, next, membersGoing }
      })
      .sort((a, b) =>
        (a.next?.startDate ?? '￿').localeCompare(b.next?.startDate ?? '￿') ||
        a.group.name.localeCompare(b.group.name),
      )
  }, [groups, nextEventPerGroup, friendInterests, myStatuses, signedInUid])

  function openCreateGroup() {
    if (isGuest) return prompt(createGroupPrompt())
    onCreateOpenChange(true)
  }

  if (error) return <ErrorState error={error} />

  return (
    <Section
      title={fbt('Groups', 'Section heading for your groups in the Crew tab')}
      action={
        <Button
          variant="outline"
          size="icon"
          className="size-7"
          onClick={openCreateGroup}
          aria-label={createGroupLabel()}
        >
          <Plus className="size-3.5" />
        </Button>
      }
    >
      {loading || authLoading ? (
        placeholders(2).map((_, i) => <CrewGroupRow key={i} loading />)
      ) : rows.length === 0 ? (
        <GroupUpsellCard onClick={openCreateGroup} />
      ) : (
        <div>
          {rows.map(({ group, next, membersGoing }) => (
            <CrewGroupRow
              key={group.id}
              group={group}
              nextEvent={next}
              goingCount={membersGoing.size}
              goingUserIds={membersGoing}
              profiles={profiles}
              locale={locale}
              onClick={() => navigate(`/groups/${group.id}`, { state: { entryPoint: 'crew' } })}
            />
          ))}
        </div>
      )}
      <CreateGroupDialog
        open={createOpen}
        onOpenChange={onCreateOpenChange}
        onCreate={createGroup}
        onCreated={id => navigate(`/groups/${id}`)}
      />
    </Section>
  )
}
